import React, { useMemo, useState } from "react";
import { Upload, X, FileText, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";
import api, { API } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import Lightbox from "@/components/Lightbox";

export function fileUrl(fileId, w) {
  const token = localStorage.getItem("abrams_token");
  return `${API}/files/${fileId}?auth=${token}${w ? `&w=${w}` : ""}`;
}

// Ridimensiona/comprimi le immagini lato client prima dell'upload (foto da telefono = 3-8MB).
async function compressImage(file, maxDim = 1600, quality = 0.8) {
  if (!file.type.startsWith("image/") || file.type === "image/gif") return file;
  try {
    const bitmap = await createImageBitmap(file);
    let { width, height } = bitmap;
    if (width <= maxDim && height <= maxDim && file.size < 900 * 1024) { bitmap.close?.(); return file; }
    const scale = Math.min(1, maxDim / Math.max(width, height));
    width = Math.round(width * scale); height = Math.round(height * scale);
    const canvas = document.createElement("canvas");
    canvas.width = width; canvas.height = height;
    canvas.getContext("2d").drawImage(bitmap, 0, 0, width, height);
    bitmap.close?.();
    const blob = await new Promise((res) => canvas.toBlob(res, "image/jpeg", quality));
    if (!blob || blob.size >= file.size) return file;
    const base = (file.name || "foto").replace(/\.[^.]+$/, "");
    return new File([blob], `${base}.jpg`, { type: "image/jpeg" });
  } catch { return file; }
}

export default function MediaUploader({ media = [], onChange, testid, multiple = false, accept = "image/*,application/pdf", readOnly = false }) {
  const { t } = useI18n();
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [lightbox, setLightbox] = useState(null);

  const imgItems = useMemo(() => (media || []).filter((m) => (m.content_type || "").startsWith("image/")), [media]);

  const upload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setBusy(true);
    setProgress({ done: 0, total: files.length });
    const uploaded = [];
    let failed = 0;
    let done = 0;
    const queue = files.slice();
    const CONCURRENCY = 3;
    const worker = async () => {
      while (queue.length) {
        const file = queue.shift();
        try {
          const compressed = await compressImage(file);
          const fd = new FormData();
          fd.append("file", compressed);
          const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
          uploaded.push(data);
        } catch (err) { failed++; }
        done++;
        setProgress({ done, total: files.length });
      }
    };
    try {
      await Promise.all(Array.from({ length: Math.min(CONCURRENCY, files.length) }, worker));
      if (uploaded.length) onChange([...(media || []), ...uploaded]);
      if (uploaded.length) toast.success(`${uploaded.length} file caricati`);
      if (failed) toast.error(`${failed} file non caricati`);
    } finally {
      setBusy(false);
      setProgress({ done: 0, total: 0 });
      e.target.value = "";
    }
  };

  const remove = (id) => onChange((media || []).filter((m) => m.file_id !== id));

  return (
    <div data-testid={testid}>
      <div className="flex flex-wrap gap-2 mb-2">
        {(media || []).map((m) => {
          const isImg = (m.content_type || "").startsWith("image/");
          return (
            <div key={m.file_id} className="relative group border border-[#cbd3e0] rounded-[4px] overflow-hidden bg-[#f3f5f9] shadow-sm hover:border-[#33559d] hover:shadow transition-all" style={{ width: 72, height: 72 }}>
              {isImg ? (
                <button type="button" data-testid={`media-open-${m.file_id}`}
                  onClick={() => setLightbox(imgItems.findIndex((x) => x.file_id === m.file_id))}
                  className="block w-full h-full cursor-zoom-in">
                  <img src={fileUrl(m.file_id, 160)} alt={m.filename} loading="lazy" decoding="async" className="w-full h-full object-cover"
                    onError={(e) => { e.currentTarget.style.display = "none"; if (e.currentTarget.nextSibling) e.currentTarget.nextSibling.style.display = "flex"; }} />
                  <div className="w-full h-full flex-col items-center justify-center text-[#8a93a3]" style={{ display: "none" }}>
                    <ImageIcon size={20} /><span className="text-[7px] mt-1 px-1 truncate max-w-full">{m.filename || "img"}</span>
                  </div>
                </button>
              ) : (
                <a href={fileUrl(m.file_id)} target="_blank" rel="noreferrer" className="block w-full h-full">
                  <div className="w-full h-full flex flex-col items-center justify-center text-[#9c1e2b]"><FileText size={22} /><span className="text-[8px] mt-1">PDF</span></div>
                </a>
              )}
              {!readOnly && (
                <button type="button" onClick={() => remove(m.file_id)} className="absolute top-0 right-0 bg-black/60 text-white p-0.5 opacity-0 group-hover:opacity-100 transition-opacity rounded-bl-[4px]">
                  <X size={12} />
                </button>
              )}
            </div>
          );
        })}
        {(!media || media.length === 0) && <span className="text-[11px] t-faint self-center">{t("no_media")}</span>}
      </div>
      {!readOnly && (
        <label className="btn-ghost btn-industrial inline-flex items-center gap-2 px-3 py-1.5 rounded-[3px] text-xs cursor-pointer">
          {busy ? <ImageIcon size={13} className="animate-pulse" /> : <Upload size={13} />}
          {busy ? (progress.total ? `${t("uploading")} ${progress.done}/${progress.total}` : t("uploading")) : t("upload_media")}
          <input type="file" accept={accept} multiple={multiple} className="hidden" onChange={upload} disabled={busy} />
        </label>
      )}
      {lightbox != null && lightbox >= 0 && imgItems.length > 0 && (
        <Lightbox items={imgItems} index={lightbox} onClose={() => setLightbox(null)} />
      )}
    </div>
  );
}
